import { useCallback, useEffect, useMemo, useState } from "react";

import {
	ICatalogue,
	ICatalogueCustomization,
	ICatalogueSetItem,
} from "../../apis/useGetCatalogueData/types";
import { removeEmptyData } from "../../common/helpers/general";
import { useGlobalEvent } from "../../common/hooks/useGlobalEvent";
import { Nullable } from "../../common/types/types";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { PAGE_PARAM } from "../constants";
import { PDP_EVENT } from "./constants";
import { getSelectedCustomization, getSelectedItem } from "./helpers";
import { ICustomizationSetItem } from "./selections/setitems/types";

interface ISetItemPayload {
	idx: number;
	setitem: ICatalogueSetItem;
	items: ICatalogue[];
	selected: string[];
	price_diff: number;
}

interface ICustomizationPayload {
	idx: number;
	customization: ICatalogueCustomization;
	selected: string[];
	setitem?: ICustomizationSetItem;
	price_diff: number;
}

export const useProductDetailPageSelectionData = (
	item?: ICatalogue,
	mode?: string,
	cartItemIndex?: number | null
) => {
	const [selectedSetItems, setSelectedSetItems] = useState<Nullable<ICatalogueSetItem>[]>([]);
	const [selectedCustomizations, setSelectedCustomizations] = useState<
		Nullable<ICatalogueCustomization>[]
	>([]);

	const cartContext = useCartContext((state) => state);

	// Initialize from cart data when in edit mode
	useEffect(() => {
		if (cartItemIndex === null) return;
		if (cartItemIndex === undefined) return;
		if (mode !== PAGE_PARAM.EDIT) return;

		const cartItem = cartContext.state.cart[cartItemIndex];

		if (cartItem) {
			if (cartItem.food_supplier_set_items) {
				setSelectedSetItems(cartItem.food_supplier_set_items);
			}
			if (cartItem.food_supplier_customizations) {
				setSelectedCustomizations(cartItem.food_supplier_customizations);
			}
		}
	}, [mode, cartItemIndex, cartContext.state.cart]);

	const updateSetItem = useCallback(
		({ idx, setitem, items, selected, price_diff }: ISetItemPayload) => {
			setSelectedSetItems((prev) => {
				const newprev = [...prev];

				while (newprev.length < idx) newprev.push(null);
				const item = getSelectedItem(items, selected, setitem.external_id, setitem)[0];

				// Check if the update is actually needed
				const currentItem = newprev[idx];
				const newItem = item
					? {
							...item,
							price_diff,
							// Preserve existing customizations if they exist
							food_supplier_customizations: currentItem?.food_supplier_customizations || [],
						}
					: null;

				const isSameItem = () => {
					if (!currentItem && !newItem) return true;
					if (!currentItem || !newItem) return false;

					// Compare the actual selected item properties, not just id
					const currentSelectedId = currentItem.id || currentItem.food_supplier_menu_item_id;
					const newSelectedId = newItem.id || newItem.food_supplier_menu_item_id;

					return (
						currentSelectedId === newSelectedId && currentItem.price_diff === newItem.price_diff
					);
				};

				if (isSameItem()) {
					return prev;
				}

				if (item) {
					newprev[idx] = newItem;
				}

				return newprev;
			});
		},
		[]
	);

	const updateCustomization = useCallback(
		({ idx, customization, selected }: ICustomizationPayload) => {
			setSelectedCustomizations((prev) => {
				const newprev = [...prev];

				while (newprev.length < idx) newprev.push(null);
				newprev[idx] = getSelectedCustomization(customization, selected);

				return newprev;
			});
		},
		[]
	);

	const updateSetItemCustomization = useCallback(
		({ idx, customization, selected, setitem }: ICustomizationPayload) => {
			setSelectedSetItems((prev) => {
				const newprev = [...prev];
				const itemidx = setitem!.idx;

				// Create a new object to avoid read-only property error
				const currentItem = newprev[itemidx];

				if (currentItem) {
					const existingCustomizations = currentItem.food_supplier_customizations
						? [...currentItem.food_supplier_customizations]
						: [];

					while (existingCustomizations.length < idx) {
						existingCustomizations.push(null);
					}

					const newCustomization = getSelectedCustomization(customization, selected);
					const existingCustomization = existingCustomizations[idx];

					// Check if the customization is actually different
					const isSameCustomization = () => {
						if (!existingCustomization && !newCustomization) return true;
						if (!existingCustomization || !newCustomization) return false;

						return JSON.stringify(existingCustomization) === JSON.stringify(newCustomization);
					};

					if (isSameCustomization()) {
						return prev;
					}

					existingCustomizations[idx] = newCustomization;

					newprev[itemidx] = {
						...currentItem,
						food_supplier_customizations: existingCustomizations,
					};
				}

				return newprev;
			});
		},
		[]
	);

	useGlobalEvent(PDP_EVENT.UPDATE_SETITEM, updateSetItem);
	useGlobalEvent(PDP_EVENT.UPDATE_CUSTOMIZATION, updateCustomization);
	useGlobalEvent(PDP_EVENT.UPDATE_SETITEM_CUSTOMIZATION, updateSetItemCustomization);

	const data = useMemo((): Nullable<ICatalogue> => {
		if (!item) return null;

		return {
			...item,
			quantity: 1,
			food_supplier_set_items: removeEmptyData(selectedSetItems),
			food_supplier_customizations: removeEmptyData(selectedCustomizations),
		};
	}, [item, selectedSetItems, selectedCustomizations]);

	return {
		data,
		selectedSetItems,
		selectedCustomizations,
	};
};
